import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useStore } from '../../../../store';
import { RecommendationGraphView } from '../../RecommendationGraphView';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialRecommendations } from '../../report/editorial';

interface Props {
  report: SiteRagReport;
  label: string;
}

export const ExecutiveSummaryActionPlanSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const recs = report.recommendations ?? [];
  const lines = recs.map(r => (typeof r === 'string' ? r : r.title)).filter(Boolean);
  const graphEntry = useStore((s) => (
    report.siteId ? s.recommendationGraphBySite[report.siteId] : undefined
  ));
  const hasRoadmap = Boolean(graphEntry?.graph?.nodes.length);

  return (
    <SectionShell label={label} hasData={lines.length > 0 || hasRoadmap} report={report} sectionId="actionPlan">
      {hasRoadmap && report.siteId && (
        <div
          className="mb-4 pt-3"
          style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
          data-testid="exec-summary-roadmap-preview"
        >
          <p
            className="text-xs font-medium mb-2"
            style={{
              fontFamily: 'var(--report-body-font, inherit)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: roleColor(theme, 'muted'),
            }}
          >
            Cross-report roadmap preview
          </p>
          <RecommendationGraphView
            site={{ id: report.siteId } as import('../../../../types/site').SiteEntity}
            compact
            graphOverride={graphEntry!.graph}
          />
        </div>
      )}
      <EditorialRecommendations
        theme={theme}
        items={lines}
        marker={theme.headingScale === 'compact' ? 'decimal' : 'roman'}
      />
    </SectionShell>
  );
};
